import { supabase } from './supabase';
import { setDocumentOutput, type WorkDocumentOutput } from './work-document-output';

export type WorkSearchCursor = { at: string; key: string };
export type WorkSearchScope = 'all' | 'active' | 'waiting' | 'blocked' | 'history' | 'results';
export type WorkSearchItem = {
  item_key: string;
  item_at: string;
  kind: 'work' | 'result' | 'document';
  subject_id: string;
  subject_label: string;
  subject_kind: string;
  title: string;
  excerpt: string;
  source_kind: 'topic' | 'task_action' | 'flow';
  source_id: string;
  version: number | null;
  flow_id: string | null;
  application_label: string | null;
  capability_key: string | null;
  output?: WorkDocumentOutput;
};

const PAGE_SIZE = 20;
const CURSOR_KEY = /^(?:topic|task|flow):[0-9a-f-]{36}$|^document:[0-9a-f-]{36}:[0-9]+$/;
const RETURN_POSITION_KEY = 'domino-work-search-return-position';

export const rememberWorkSearchPosition = (key: string, top: number): void => {
  sessionStorage.setItem(RETURN_POSITION_KEY, `${key}|${top}`);
};

export const readWorkSearchPosition = (key: string): number | null => {
  const stored = sessionStorage.getItem(RETURN_POSITION_KEY);
  if (!stored?.startsWith(`${key}|`)) return null;
  const top = Number(stored.slice(key.length + 1));
  return Number.isFinite(top) && top >= 0 ? top : null;
};

export const readWorkSearchCursors = (params: URLSearchParams): WorkSearchCursor[] => {
  const values = params.getAll('c');
  const cursors: WorkSearchCursor[] = [];
  for (const value of values) {
    const separator = value.indexOf('|');
    const at = value.slice(0, separator);
    const key = value.slice(separator + 1);
    if (separator < 0 || !Number.isFinite(Date.parse(at)) || !CURSOR_KEY.test(key)) return [];
    cursors.push({ at, key });
  }
  return cursors;
};

export const searchWork = async (
  query: string,
  cursor: WorkSearchCursor | null,
  accountId: string | null = null,
  scope: WorkSearchScope = 'all',
  flowId: string | null = null,
  capabilityKey: string | null = null,
  objectiveId: string | null = null,
) => {
  const { data, error } = await supabase.rpc('search_work_b', {
    p_query: query,
    p_before_at: cursor?.at ?? null,
    p_before_key: cursor?.key ?? null,
    p_limit: PAGE_SIZE + 1,
    p_account_id: accountId,
    p_scope: scope,
    p_flow_id: flowId,
    p_capability_key: capabilityKey,
    p_objective_id: objectiveId,
  });
  if (error || !Array.isArray(data)) throw new Error('La recherche n’a pas pu être chargée.');
  const items = data.slice(0, PAGE_SIZE) as WorkSearchItem[];
  // Keep the existing RPC/cursor contract. Resolve output identities in one JWT-
  // scoped batch; a missing identity must not open a different legacy document.
  const documentIds = items.flatMap(item => {
    const id = /^document:([0-9a-f-]{36}):\d+$/.exec(item.item_key)?.[1];
    return item.kind === 'document' && id ? [id] : [];
  });
  if (documentIds.length) {
    const { data: documents, error: readError } = await supabase.from('work_documents')
      .select('id,source_kind,source_id,result_tab_id,result_section_id').in('id', documentIds);
    if (readError || !documents) throw new Error('Les références des documents n’ont pas pu être chargées.');
    for (const item of items) {
      const id = /^document:([0-9a-f-]{36}):\d+$/.exec(item.item_key)?.[1];
      if (item.kind !== 'document' || !id) continue;
      const document = documents.find(candidate => candidate.id === id && candidate.source_kind === item.source_kind && candidate.source_id === item.source_id);
      if (!document) throw new Error('Un document n’est plus accessible. Relancez la recherche.');
      if (document.result_tab_id && document.result_section_id) {
        item.output = { tabId: document.result_tab_id, sectionId: document.result_section_id };
      }
    }
  }
  const last = items.at(-1);
  return {
    items,
    nextCursor: data.length > PAGE_SIZE && last
      ? { at: last.item_at, key: last.item_key }
      : null,
  };
};

export const workSearchPath = (item: WorkSearchItem): string => {
  if (item.source_kind === 'flow' && item.output) {
    const params = new URLSearchParams({ [item.flow_id ? 'run' : 'flow']: item.source_id });
    if (item.version !== null) params.set('version', String(item.version));
    setDocumentOutput(params, item.output);
    return `${item.flow_id ? `/tools/${encodeURIComponent(item.flow_id)}` : `/threads/${encodeURIComponent(item.subject_id)}`}?${params.toString()}`;
  }
  if (item.source_kind === 'flow' && item.flow_id) {
    return `/tools/${encodeURIComponent(item.flow_id)}?run=${encodeURIComponent(item.source_id)}${item.version !== null ? `&version=${item.version}` : ''}`;
  }
  if (item.source_kind === 'flow') {
    return `/executions/${encodeURIComponent(item.source_id)}${item.version !== null ? `?run=${encodeURIComponent(item.source_id)}&version=${item.version}` : ''}`;
  }
  const params = new URLSearchParams();
  params.set(item.source_kind === 'topic' ? 'topic' : 'result', item.source_id);
  if (item.version !== null) params.set('version', String(item.version));
  return `/threads/${encodeURIComponent(item.subject_id)}?${params.toString()}`;
};
